import type { ModuleConfig, User } from '../types/config';
import type { ModuleAccessLevel } from './moduleAccessPolicy';
import type { PlatformModuleCatalogEntry } from '../services/api/types/modules';
import type {
  ModuleAccessSummary,
  RoleModuleAccessSummary,
  RoleSubmoduleAccessSummary,
  UpdateRoleModuleAccessRequest,
} from '../services/api/types/rbac';
import {
  aggregateModuleLevel,
  applyModuleLevelToSubmodules,
  buildSubmoduleSavePayload,
  submoduleKey,
} from '../utils/submoduleAccess';

export type StaffHubPanelId = 'staff' | 'teachers';

export type StaffHubTab = {
  id: string;
  label: string;
  panel: StaffHubPanelId;
  canWrite: boolean;
};

export const STAFF_PEOPLE_SUBMODULES: Array<{
  submodule_id: StaffHubPanelId;
  display_name: string;
  panel: StaffHubPanelId;
}> = [
  { submodule_id: 'staff', display_name: 'Staff', panel: 'staff' },
  { submodule_id: 'teachers', display_name: 'Teachers', panel: 'teachers' },
];

const DEFAULT_PANEL_BY_SUBMODULE: Record<string, StaffHubPanelId> = {
  staff: 'staff',
  office_staff: 'staff',
  non_teaching: 'staff',
  teachers: 'teachers',
  teacher: 'teachers',
};

const TAB_ALIASES: Record<string, string> = {
  staff: 'staff',
  teachers: 'teachers',
  teacher: 'teachers',
};

function normalizeId(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export function isStaffModuleCode(code: string | null | undefined): boolean {
  return normalizeId(code ?? '') === 'staff';
}

export function isLegacyTeachersModuleCode(code: string | null | undefined): boolean {
  const id = normalizeId(code ?? '');
  return id === 'teachers' || id === 'teacher';
}

export function staffPeoplePanelForSubmodule(submoduleId: string): StaffHubPanelId | null {
  return DEFAULT_PANEL_BY_SUBMODULE[normalizeId(submoduleId)] ?? null;
}

function findModuleEntry(
  moduleAccess: NonNullable<User['moduleAccess']> | RoleModuleAccessSummary[] | undefined,
  predicate: (code: string) => boolean
): ModuleAccessSummary | RoleModuleAccessSummary | undefined {
  return (moduleAccess ?? []).find((entry) => predicate(normalizeId(entry.module_code ?? '')));
}

function findStaffModuleEntry(
  moduleAccess: NonNullable<User['moduleAccess']> | undefined
): ModuleAccessSummary | undefined {
  return findModuleEntry(moduleAccess, (code) => code === 'staff') as ModuleAccessSummary | undefined;
}

function findTeachersModuleEntry(
  moduleAccess: NonNullable<User['moduleAccess']> | undefined
): ModuleAccessSummary | undefined {
  return findModuleEntry(
    moduleAccess,
    (code) => code === 'teachers' || code === 'teacher'
  ) as ModuleAccessSummary | undefined;
}

function levelFromEntry(
  entry: ModuleAccessSummary | RoleModuleAccessSummary | undefined
): ModuleAccessLevel {
  if (!entry || entry.enabled === false || entry.access_level === 'none') return 'none';
  if (entry.access_level === 'write') return 'write';
  if (entry.access_level === 'read') return 'read';
  return 'none';
}

function accessRank(level: ModuleAccessLevel): number {
  if (level === 'write') return 2;
  if (level === 'read') return 1;
  return 0;
}

function higherAccessLevel(a: ModuleAccessLevel, b: ModuleAccessLevel): ModuleAccessLevel {
  return accessRank(a) >= accessRank(b) ? a : b;
}

function levelFromSubmodule(sub: RoleSubmoduleAccessSummary | undefined): ModuleAccessLevel {
  if (!sub || sub.access_level === 'none') return 'none';
  if (sub.access_level === 'write') return 'write';
  if (sub.access_level === 'read') return 'read';
  return 'none';
}

function peopleSubmodules(
  entry: ModuleAccessSummary | RoleModuleAccessSummary | undefined
): RoleSubmoduleAccessSummary[] {
  return (entry?.submodules ?? []).filter((sub) => staffPeoplePanelForSubmodule(sub.submodule_id));
}

/**
 * Effective access for the Staff hub Staff / Teachers tabs.
 * Prefers the matching staff-module submodule; falls back to the legacy
 * Teachers module for the teachers tab, then to the Staff module as a whole.
 *
 * The standalone `teachers` catalog row is screen_only, so login always reports
 * read even when the role has Staff write (`staff:manage`). Staff write must
 * win so admin/owner can add and edit teachers.
 */
export function resolveStaffPanelAccessLevel(
  user: Pick<User, 'moduleAccess'> | undefined,
  panel: StaffHubPanelId
): ModuleAccessLevel {
  const staffEntry = findStaffModuleEntry(user?.moduleAccess);
  const peopleSubs = peopleSubmodules(staffEntry);
  const match = peopleSubs.find((sub) => staffPeoplePanelForSubmodule(sub.submodule_id) === panel);
  const staffLevel = levelFromEntry(staffEntry);

  if (panel === 'teachers') {
    if (match && levelFromSubmodule(match) === 'none') return 'none';
    const teacherLevel = levelFromEntry(findTeachersModuleEntry(user?.moduleAccess));
    const subLevel = match ? levelFromSubmodule(match) : 'none';
    return higherAccessLevel(higherAccessLevel(staffLevel, teacherLevel), subLevel);
  }

  if (match) return levelFromSubmodule(match);
  return staffLevel;
}

export function userCanOpenStaffHub(user: Pick<User, 'moduleAccess'> | undefined): boolean {
  return (
    resolveStaffPanelAccessLevel(user, 'staff') !== 'none' ||
    resolveStaffPanelAccessLevel(user, 'teachers') !== 'none'
  );
}

export function resolveStaffHubTabs(
  user: User | undefined,
  _moduleConfig?: ModuleConfig
): StaffHubTab[] {
  const tabs: StaffHubTab[] = [];
  for (const spec of STAFF_PEOPLE_SUBMODULES) {
    const level = resolveStaffPanelAccessLevel(user, spec.panel);
    if (level === 'none') continue;
    tabs.push({
      id: spec.submodule_id,
      label: spec.display_name,
      panel: spec.panel,
      canWrite: level === 'write',
    });
  }
  return tabs;
}

export function resolveStaffHubTabFromUrl(raw: string | null, tabs: StaffHubTab[]): string {
  if (!tabs.length) return '';
  if (!raw?.trim()) return tabs[0].id;
  const needle = normalizeId(raw);
  const aliased = TAB_ALIASES[needle] ?? needle;
  const hit = tabs.find((tab) => tab.id === aliased || tab.id === needle || tab.panel === needle);
  return hit?.id ?? tabs[0].id;
}

/** Drop the standalone Teachers catalog row; rewrite it to Staff when Staff is missing. */
export function mergeStaffPeopleCatalog(
  catalog: PlatformModuleCatalogEntry[]
): PlatformModuleCatalogEntry[] {
  const staff = catalog.find((entry) => isStaffModuleCode(entry.module_code));
  const teachers = catalog.find((entry) => isLegacyTeachersModuleCode(entry.module_code));
  const rest = catalog.filter((entry) => !isLegacyTeachersModuleCode(entry.module_code));
  if (staff || !teachers) return rest;

  return [
    ...rest,
    {
      ...teachers,
      module_code: 'staff',
      display_name:
        teachers.display_name?.trim() && !/^teachers?$/i.test(teachers.display_name.trim())
          ? teachers.display_name
          : 'Staff',
      route_path: '/staff',
      component: 'Staff',
      screen_id: 'staff',
    },
  ];
}

function submoduleForPanel(
  subs: RoleSubmoduleAccessSummary[],
  panel: StaffHubPanelId
): RoleSubmoduleAccessSummary | undefined {
  return subs.find((sub) => staffPeoplePanelForSubmodule(sub.submodule_id) === panel);
}

function makePeopleSubmodule(
  spec: (typeof STAFF_PEOPLE_SUBMODULES)[number],
  accessLevel: ModuleAccessLevel,
  existing?: RoleSubmoduleAccessSummary
): RoleSubmoduleAccessSummary {
  return {
    submodule_id: existing?.submodule_id ?? spec.submodule_id,
    display_name: existing?.display_name?.trim() || spec.display_name,
    access_level: existing?.access_level ?? accessLevel,
    supports_read: existing?.supports_read ?? true,
    supports_write: existing?.supports_write ?? true,
  };
}

/**
 * Permissions UI: keep a single Staff module with Staff + Teachers submodules.
 * Legacy standalone Teachers is folded into the Teachers submodule.
 */
export function foldStaffPeopleModules(
  modules: RoleModuleAccessSummary[]
): RoleModuleAccessSummary[] {
  const staff = modules.find((module) => isStaffModuleCode(module.module_code));
  const teachers = modules.find((module) => isLegacyTeachersModuleCode(module.module_code));
  if (!staff && !teachers) return modules;

  const others = modules.filter(
    (module) => !isStaffModuleCode(module.module_code) && !isLegacyTeachersModuleCode(module.module_code)
  );

  const staffLevel = levelFromEntry(staff);
  const teacherLevel = higherAccessLevel(
    teachers ? levelFromEntry(teachers) : 'none',
    staffLevel
  );
  const existingSubs = staff?.submodules ?? [];
  const extras = existingSubs.filter((sub) => !staffPeoplePanelForSubmodule(sub.submodule_id));

  const foldedSubs: RoleSubmoduleAccessSummary[] = [
    ...extras,
    ...STAFF_PEOPLE_SUBMODULES.map((spec) => {
      const existing = submoduleForPanel(existingSubs, spec.panel);
      const fallback = spec.panel === 'teachers' ? teacherLevel : staffLevel;
      const folded = makePeopleSubmodule(spec, fallback, existing);
      if (spec.panel !== 'teachers') return folded;
      return {
        ...folded,
        access_level: higherAccessLevel(
          (folded.access_level as ModuleAccessLevel) || 'none',
          fallback
        ),
      };
    }),
  ];

  const enabled = Boolean(staff?.enabled || teachers?.enabled);
  const folded: RoleModuleAccessSummary = {
    module_code: 'staff',
    display_name: staff?.display_name?.trim() || 'Staff',
    enabled,
    access_level: enabled
      ? aggregateModuleLevel(foldedSubs) === 'custom'
        ? 'read'
        : aggregateModuleLevel(foldedSubs)
      : 'none',
    supports_write: staff?.supports_write !== false || teachers?.supports_write !== false,
    submodules: foldedSubs,
  };

  return [...others, folded];
}

function moduleAccessFromSubmodules(
  module: RoleModuleAccessSummary,
  levels: Record<string, ModuleAccessLevel>
): ModuleAccessLevel {
  const subs = module.submodules ?? [];
  if (!subs.length) {
    return (levels[submoduleKey(module.module_code, module.module_code)] as ModuleAccessLevel) || 'none';
  }
  const aggregated = aggregateModuleLevel(
    subs.map((sub) => ({
      ...sub,
      access_level: levels[submoduleKey(module.module_code, sub.submodule_id)] ?? 'none',
    }))
  );
  return aggregated === 'custom' ? 'write' : aggregated;
}

/** Persist Staff submodules and mirror Teachers onto the legacy Teachers module when it existed. */
export function buildStaffPeopleSavePayload(
  foldedModules: RoleModuleAccessSummary[],
  levels: Record<string, ModuleAccessLevel>,
  originalModules: RoleModuleAccessSummary[]
): UpdateRoleModuleAccessRequest {
  const submodulePayload = buildSubmoduleSavePayload(foldedModules, levels);
  const teacherLevel =
    (levels[submoduleKey('staff', 'teachers')] as ModuleAccessLevel | undefined) ??
    (levels[submoduleKey('staff', 'teacher')] as ModuleAccessLevel | undefined) ??
    'none';
  const staffModule = foldedModules.find((module) => isStaffModuleCode(module.module_code));
  const staffLevel = staffModule ? moduleAccessFromSubmodules(staffModule, levels) : 'none';

  const originalTeachers = originalModules.find((module) =>
    isLegacyTeachersModuleCode(module.module_code)
  );

  const moduleEntries = foldedModules
    .filter((module) => module.enabled)
    .map((module) => ({
      module_code: module.module_code,
      access_level: moduleAccessFromSubmodules(module, levels),
    }));

  if (originalTeachers && !moduleEntries.some((entry) => isLegacyTeachersModuleCode(entry.module_code))) {
    moduleEntries.push({
      module_code: originalTeachers.module_code,
      access_level: teacherLevel,
    });
  }

  let submodules = submodulePayload.submodules ?? [];
  if (originalTeachers?.submodules?.length) {
    let teacherLevels: Record<string, ModuleAccessLevel> = {};
    teacherLevels = applyModuleLevelToSubmodules(originalTeachers, teacherLevel, teacherLevels);
    submodules = [
      ...submodules,
      ...(buildSubmoduleSavePayload([originalTeachers], teacherLevels).submodules ?? []),
    ];
  }

  return {
    modules: moduleEntries.length ? moduleEntries : [{ module_code: 'staff', access_level: staffLevel }],
    submodules,
  };
}

export function foldStaffPeopleSubscriptions<
  T extends { module_code: string; display_name?: string | null; enabled: boolean },
>(rows: T[]): T[] {
  const staff = rows.find((row) => isStaffModuleCode(row.module_code));
  const teachers = rows.find((row) => isLegacyTeachersModuleCode(row.module_code));
  if (!staff && !teachers) return rows;
  const rest = rows.filter(
    (row) => !isStaffModuleCode(row.module_code) && !isLegacyTeachersModuleCode(row.module_code)
  );
  if (staff) {
    return [...rest, { ...staff, enabled: staff.enabled || Boolean(teachers?.enabled) }];
  }
  return [
    ...rest,
    {
      ...teachers!,
      module_code: 'staff',
      display_name: 'Staff',
      enabled: Boolean(teachers?.enabled),
    },
  ];
}

export type StaffPeopleToggleSubmodule = {
  submodule_id: string;
  display_name: string;
  supports_read: boolean;
  supports_write: boolean;
  enabled: boolean;
};

export type StaffPeopleToggleRow = {
  module_code: string;
  display_name?: string | null;
  enabled: boolean;
  submodules: StaffPeopleToggleSubmodule[];
};

export function catalogHasLegacyTeachersModule<T extends { module_code: string }>(rows: T[]): boolean {
  return rows.some((row) => isLegacyTeachersModuleCode(row.module_code));
}

/** Super Admin: Staff module with Staff + Teachers sub-features; hide standalone Teachers. */
export function foldStaffPeopleToggleRows<T extends StaffPeopleToggleRow>(rows: T[]): T[] {
  const staff = rows.find((row) => isStaffModuleCode(row.module_code));
  const teachers = rows.find((row) => isLegacyTeachersModuleCode(row.module_code));
  if (!staff && !teachers) return rows;

  const rest = rows.filter(
    (row) => !isStaffModuleCode(row.module_code) && !isLegacyTeachersModuleCode(row.module_code)
  );
  const base = staff ??
    ({
      ...teachers!,
      module_code: 'staff',
      display_name: 'Staff',
      enabled: Boolean(teachers?.enabled),
      submodules: [],
    } as T);

  const existing = base.submodules ?? [];
  const extras = existing.filter((sub) => !staffPeoplePanelForSubmodule(sub.submodule_id));
  const staffSub = existing.find((sub) => staffPeoplePanelForSubmodule(sub.submodule_id) === 'staff');
  const teacherSub = existing.find((sub) => staffPeoplePanelForSubmodule(sub.submodule_id) === 'teachers');

  return [
    ...rest,
    {
      ...base,
      display_name: base.display_name?.trim() || 'Staff',
      enabled: base.enabled || Boolean(teachers?.enabled),
      submodules: [
        ...extras,
        staffSub ?? {
          submodule_id: 'staff',
          display_name: 'Staff',
          supports_read: true,
          supports_write: true,
          enabled: base.enabled,
        },
        teacherSub ?? {
          submodule_id: 'teachers',
          display_name: 'Teachers',
          supports_read: true,
          supports_write: true,
          enabled: teachers?.enabled ?? base.enabled,
        },
      ],
    },
  ];
}

export function expandStaffPeopleToggleSave<T extends StaffPeopleToggleRow>(
  folded: T[],
  originalHadTeachers: boolean
): Array<{
  module_code: string;
  enabled: boolean;
  submodules: Array<{ submodule_id: string; enabled: boolean }>;
}> {
  const staff = folded.find((row) => isStaffModuleCode(row.module_code));
  const teacherSub = staff?.submodules.find(
    (sub) => staffPeoplePanelForSubmodule(sub.submodule_id) === 'teachers'
  );
  const updates = folded.map((module) => ({
    module_code: module.module_code,
    enabled: module.enabled,
    submodules: module.submodules.map((sub) => ({
      submodule_id: sub.submodule_id,
      enabled: module.enabled && sub.enabled,
    })),
  }));

  if (
    originalHadTeachers &&
    staff &&
    !folded.some((row) => isLegacyTeachersModuleCode(row.module_code))
  ) {
    updates.push({
      module_code: 'teachers',
      enabled: Boolean(staff.enabled && (teacherSub?.enabled ?? staff.enabled)),
      submodules: [],
    });
  }

  return updates;
}
